import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { LenseConfig, Scenario } from 'types/pricingSimulator';
import { pricingSimulatorUrls } from 'config/urls';
import { sendNotification } from 'utils/helperMethods';
import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import { queryClient } from 'services/reactQueryService';

interface Props {
    selectedScenarioInfo: Scenario | null;
}

interface outputType {
    loading: boolean;
    mutate: UseMutateFunction<AxiosResponse<any, any> | undefined, Error, any, unknown>;
}

function useUpdateLenseConfig({ selectedScenarioInfo }: Props): outputType {
    // Queries
    const { isPending, isPaused, mutate } = useMutation({
        mutationFn: async (inputData: any) => {
            //write onsuccess and onerror where mutate function is called
            try {
                const updatedLenseConfig = getLenseConfigPayload(inputData.prevState, inputData.currentState);
                const payload = {
                    scenario_id: inputData.scenarioId?.toString(),
                    user_email: inputData.userId,
                    lens_kpi_ticked_status: updatedLenseConfig,
                };
                const url = pricingSimulatorUrls.postUpdateLenseConfig;

                const data = await axios({
                    method: 'post',
                    url,
                    data: { ...payload },
                });
                return data;
            } catch (error) {
                console.log('err ', error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.refetchQueries({
                exact: false,
                queryKey: [
                    'ps-lense-config',
                    {
                        scenario_id: String(selectedScenarioInfo?.scenarioId),
                        user_email: localStorage.getItem('email') || '',
                    },
                ],
            });

            sendNotification('success', PricingSimulatorNotificationMessages.LENS_CONFIG_SUCCESS);
        },
        onError: (error) => {
            sendNotification('error', PricingSimulatorNotificationMessages.LENS_CONFIG_FAILED);
        },
    });
    const apiLoading = isPaused || isPending;

    const outputData: outputType = {
        loading: apiLoading,
        mutate,
    };
    return outputData;
}

export default useUpdateLenseConfig;

const getLenseConfigPayload = (prevState: LenseConfig[], currentState: LenseConfig[]) => {
    const prevSelectedAttributes = getSelectedIds(prevState);
    const newSelectedAttributes = getSelectedIds(currentState);

    const unTickedLenses: any[] = [];
    // add all those which were earlier ticked but now unticked
    newSelectedAttributes.ticked.forEach((lens: any, index: number) => {
        const lenseObj = {
            lens_id: lens.lens_id,
            kpi_id: [],
        };
        lenseObj.kpi_id = prevSelectedAttributes.ticked[index].kpi_id.filter((i: any) => !lens.kpi_id.includes(i));

        unTickedLenses.push(lenseObj);
    });

    // filtering all those lenses which has 0 kpi_id
    const res = {
        ticked: newSelectedAttributes?.ticked.filter((l: any) => l.kpi_id.length),
        unticked: unTickedLenses.filter((l: any) => l.kpi_id.length),
    };

    return res;
};

function getSelectedIds(state: any) {
    let result: any = {
        ticked: [],
    };

    state.forEach((lens: LenseConfig) => {
        let selectedAttributes: Array<any> = [];

        lens.attributes.forEach((attr) => {
            if (attr.isSelected) {
                selectedAttributes.push(attr.attributeId);
            }
        });

        result.ticked.push({ lens_id: lens.lenseId, kpi_id: selectedAttributes });
    });

    return result;
}
